import { format as formatWithDateFns } from 'date-fns';
import { INTL_LOCALE, PortalLang, portalLanguage } from './index';

/**
 * Money and dates in the language on screen.
 *
 * English keeps the exact output the portal always had (`$199.00`, date-fns
 * patterns), so Go Oil and an English Aperio visitor see no change. French
 * follows the marketing site and the OQLF: `199,00 $`, with a no-break space
 * (U+00A0) before the `$` and as the thousands separator (Intl fr-CA does
 * both). Spanish follows the site too: `$199.00`, the Latin American
 * convention (es-419 grouping: `$1,999.50`).
 */

const NBSP = '\u00a0';

function amountParts(value: number, lang: PortalLang, whole: boolean): string {
  const digits = whole ? 0 : 2;
  if (lang === 'fr') {
    return new Intl.NumberFormat(INTL_LOCALE.fr, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value)
      // Some ICU builds use U+202F (narrow nbsp) for the group separator;
      // the site prints U+00A0 everywhere.
      .replace(/\u202f/g, NBSP);
  }
  return `$${new Intl.NumberFormat(INTL_LOCALE[lang], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)}`;
}

/**
 * A dollar amount: `$199.00` (EN, unchanged: no grouping, as before),
 * `199,00 $` (FR), `$199.00` (ES). Negative amounts keep their sign.
 */
export function formatMoney(
  value: number,
  lang: PortalLang = portalLanguage(),
): string {
  if (lang === 'en') {
    const abs = `$${Math.abs(value).toFixed(2)}`;
    return value < 0 ? `-${abs}` : abs;
  }
  const text = amountParts(Math.abs(value), lang, false);
  return value < 0 ? `-${text}` : text;
}

/** Whole dollars, for tight spots (calendar day prices): `$20` / `20 $`. */
export function formatWholeDollars(
  value: number,
  lang: PortalLang = portalLanguage(),
): string {
  const rounded = Math.round(value);
  if (lang === 'en') return `$${rounded}`;
  return amountParts(rounded, lang, true);
}

/**
 * A date or time. `enPattern` is the date-fns pattern English has always
 * used (output unchanged); French and Spanish use Intl with `options`, which
 * gets their word order, month names and 24-hour / a. m. conventions right
 * (`lundi 5 octobre`, `8 h 30`; `lunes, 5 de octubre`, `8:30 a.m.`).
 */
export function formatDate(
  date: Date,
  enPattern: string,
  options: Intl.DateTimeFormatOptions,
  lang: PortalLang = portalLanguage(),
): string {
  if (lang === 'en') return formatWithDateFns(date, enPattern);
  return new Intl.DateTimeFormat(INTL_LOCALE[lang], options)
    .format(date)
    .replace(/\u202f/g, NBSP);
}

/**
 * `8:30 am` (EN, unchanged) / `8 h 30`, `9 h` on the hour (FR, as the site
 * writes "8 h à 20 h") / `8:30 a.m.` (ES).
 */
export function formatTime(
  date: Date,
  enPattern = 'h:mm aaa',
  lang: PortalLang = portalLanguage(),
): string {
  const text = formatDate(date, enPattern, {
    hour: 'numeric',
    minute: '2-digit',
    // es-419's default clock differs between ICU versions (24 h on some):
    // the Spanish portal always shows a.m./p.m., like the English one.
    ...(lang === 'es' ? { hour12: true } : {}),
  }, lang);
  return lang === 'fr' ? text.replace(/(h)[\s\u00a0]00$/, '$1') : text;
}

/** A plain number, e.g. a tax rate: `14.975` (EN, as is) / `14,975` (FR). */
export function formatNumber(
  value: number,
  lang: PortalLang = portalLanguage(),
): string {
  if (lang === 'en') return String(value);
  return new Intl.NumberFormat(INTL_LOCALE[lang], {
    maximumFractionDigits: 3,
  }).format(value).replace(/\u202f/g, NBSP);
}

/**
 * A line that takes money off: `- $7.32` (EN, unchanged); in French and
 * Spanish the minus is held to the amount by a no-break space, so it can
 * never wrap onto a line of its own.
 */
export function formatDeduction(
  value: number,
  lang: PortalLang = portalLanguage(),
): string {
  if (lang === 'en') return `- ${formatMoney(value, lang)}`;
  return `-${NBSP}${formatMoney(value, lang)}`;
}
